import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail } from 'lucide-react';
import { config } from '@/config/env';
import { useI18n } from '@/hooks/useI18n';
import { AnuncioPro } from './AnuncioPro';
import styles from './SiteFooter.module.css';

const currentYear = new Date().getFullYear();

/**
 * Footer sitewide. Los enlaces legales son páginas con URL propia, no modales
 * como en el sitio anterior: `/barbunny/eliminar-cuenta` en particular tiene
 * que ser una URL citable desde la ficha de Google Play, y las de Ilumetria
 * desde contratos y avisos.
 */
export function SiteFooter() {
  const { t } = useI18n();
  const f = t.comun.footer;

  return (
    <footer className={styles.footer}>
      <div className="ilu-container">
        <div className={styles.grid}>
          <div>
            <div className={styles.brand}>
              <img src="/logo/isologo.png" alt="" width={32} height={32} />
              <span>ILUMETRIA</span>
            </div>
            <p className={styles.blurb}>{f.blurb}</p>
            <div className={styles.social}>
              <a
                href={config.instagramUrl}
                className="icon-button"
                aria-label={f.instagramLabel}
                target="_blank"
                rel="noreferrer noopener"
              >
                <Instagram size={16} />
              </a>
              <a
                href={config.facebookUrl}
                className="icon-button"
                aria-label={f.facebookLabel}
                target="_blank"
                rel="noreferrer noopener"
              >
                <Facebook size={16} />
              </a>
              <a
                href={`mailto:${config.contactEmail}`}
                className="icon-button"
                aria-label={f.correoLabel(config.contactEmail)}
              >
                <Mail size={16} />
              </a>
            </div>
          </div>

          {/* El `aria-label` del <nav> y el <h3> son el mismo texto a propósito:
              si divergen, el lector de pantalla anuncia una cosa y la vista otra. */}
          <nav className={styles.column} aria-label={f.plataforma.titulo}>
            <h3>{f.plataforma.titulo}</h3>
            <ul>
              <li>
                <a href="/#flujo">{f.plataforma.flujo}</a>
              </li>
              <li>
                <a href="/#soluciones">{f.plataforma.soluciones}</a>
              </li>
              <li>
                <a href="/#planes">{f.plataforma.planes}</a>
              </li>
              <li>
                <Link to="/contacto">{f.plataforma.agendarDemo}</Link>
              </li>
              <li>
                {/* La demostración pasa a ser la única puerta que el footer
                    ofrece. Antes había dos más —"Entrar al portal" y la del
                    embudo de inversión— y las dos llevaban a pantallas que se
                    fueron con el backend el 2026-09-18. */}
                <Link to="/demo">{f.plataforma.demo}</Link>
              </li>
            </ul>
          </nav>

          <nav className={styles.column} aria-label={f.legal.titulo}>
            <h3>{f.legal.titulo}</h3>
            <ul>
              <li>
                <Link to="/privacidad">{f.legal.privacidad}</Link>
              </li>
              <li>
                <Link to="/terminos">{f.legal.terminos}</Link>
              </li>
              <li>
                <Link to="/cookies">{f.legal.cookies}</Link>
              </li>
              <li>
                <Link to="/barbunny/privacidad">{f.legal.barbunnyPrivacidad}</Link>
              </li>
              <li>
                <Link to="/barbunny/eliminar-cuenta">{f.legal.barbunnyEliminarCuenta}</Link>
              </li>
            </ul>
          </nav>
        </div>

        {/* Va debajo de las redes y encima del aviso de derechos: es una
            credencial, y ese es el renglon del footer donde se leen. */}
        <AnuncioPro />

        <div className={styles.bottom}>
          <span>{f.derechos(currentYear)}</span>
          <a href={`mailto:${config.contactEmail}`}>{config.contactEmail}</a>
        </div>
      </div>
    </footer>
  );
}
